import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MessageService, ConfirmationService } from "primeng/api";
import { AdminFeaturesService } from "../service/admin-features.service";

/**
 * Transfer Requests.
 *
 * ISSO is stage 2 of four, so the screen leads with the requests actually
 * waiting on them and keeps the rest for context. Every row carries the same
 * timeline the family and both schools see, so a phone call about "where is my
 * transfer" is answered from the same words on every side.
 */
@Component({
  selector: "app-transfers",
  templateUrl: "./transfers.component.html",
  styleUrls: ["./transfers.component.css"],
  providers: [MessageService, ConfirmationService],
})
export class TransfersComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  rows: any[] = [];
  /** Rows after the search box; the table always renders this. */
  view: any[] = [];
  search = "";
  loading = false;
  filter: "open" | "" = "open";
  selected: any = null;
  note = "";
  busy = false;

  constructor(
    private svc: AdminFeaturesService,
    private messageService: MessageService,
  ) {}

  ngOnInit() { this.load(); }

  load() {
    this.loading = true;
    this.svc.listTransfers(this.filter || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (r: any[]) => { this.rows = r || []; this.applySearch(); this.loading = false; },
        () => { this.rows = []; this.view = []; this.loading = false; },
      );
  }

  setFilter(f: "open" | "") { this.filter = f; this.selected = null; this.load(); }

  /**
   * Filtered here rather than on the server: a transfer is a rare event, so the
   * whole history is a short list, and searching it locally keeps the result
   * instant while the admin is on the phone to a parent.
   */
  applySearch() {
    const q = (this.search || "").trim().toLowerCase();
    if (!q) { this.view = this.rows; return; }
    this.view = this.rows.filter(t => {
      const hay = [
        t.student && t.student.name,
        t.student && t.student.uniqueId,
        t.fromSchool && t.fromSchool.name,
        t.toSchool && t.toSchool.name,
        t.statusLabel,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }

  /** Raised, and settled - the two dates a history list is read for. */
  raisedOn(t: any): string   { return this.fmt(t.requestedAt); }
  settledOn(t: any): string {
    if (t.appliedAt) return this.fmt(t.appliedAt);
    const last = (t.timeline || []).filter((s: any) => s.at).pop();
    return t.status === "completed" || t.status === "rejected" || t.status === "cancelled"
      ? (last ? this.fmt(last.at) : "-")
      : "-";
  }
  private fmt(iso: string): string {
    if (!iso) return "-";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }

  open(row: any) { this.selected = row; this.note = ""; }
  close()        { this.selected = null; this.note = ""; }

  /** Date a step was settled, blank while it has not been. */
  stepWhen(s: any): string { return s && s.at ? this.fmt(s.at) : ""; }

  /** Who wrote the note on a step, so a reason is not mistaken for a decision. */
  stepWho(s: any): string {
    return s.key === "school"     ? "Current school"
         : s.key === "admin"      ? "ISSO"
         : s.key === "new_school" ? "New school"
         : "Student";
  }

  raisedBy(t: any): string {
    return t.requestedByType === "admin" ? "ISSO" : "Family";
  }

  changeList(t: any): { k: string; v: string }[] {
    if (!t || !t.proposedChanges) return [];
    return Object.keys(t.proposedChanges).map(k => {
      const raw = t.proposedChanges[k];
      return { k, v: raw === null || raw === undefined ? "-" : String(raw) };
    });
  }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
